import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { FiSettings, FiSave, FiDatabase, FiRefreshCw, FiAlertCircle, FiShield, FiBell, FiMail, FiSmartphone } from 'react-icons/fi';
import { buildApiUrl } from '../../config/api';

const SystemSettings = () => {
  const [settings, setSettings] = useState({
    general: {
      siteName: 'MarryCenter',
      siteDescription: 'Find Your Perfect Match',
      maintenanceMode: false,
      allowNewRegistrations: true,
      requireEmailVerification: true,
      maxUsersPerDay: 1000,
      defaultMembershipType: 'free'
    },
    security: {
      passwordMinLength: 8,
      sessionTimeout: 24,
      maxLoginAttempts: 5,
      lockoutDuration: 30,
      twoFactorAuth: false,
      ipWhitelist: '',
      allowedFileTypes: 'jpg,jpeg,png,pdf',
      maxFileSize: 5
    },
    notifications: {
      emailNotifications: true,
      pushNotifications: true,
      smsNotifications: false,
      welcomeEmail: true,
      matchNotifications: true,
      messageNotifications: true,
      profileViewNotifications: false,
      systemAlerts: true
    },
    messaging: {
      maxMessageLength: 1000,
      messageRetentionDays: 365,
      allowImageMessages: true,
      autoDeleteOldMessages: false,
      messageFiltering: true,
      blockInappropriateContent: true,
      rateLimitPerHour: 50
    },
    backup: {
      autoBackup: true,
      backupFrequency: 'daily',
      backupRetention: 30,
      backupLocation: 'cloud',
      emailBackupReports: true,
      includeUserMedia: true
    },
    mobileApp: {
      android: {
        minVersionCode: 1,
        latestVersion: '1.0.0',
        updateUrl: 'https://play.google.com/store/apps/details?id=com.marrycenter.app',
        forceUpdate: false
      },
      ios: {
        minVersionCode: 1,
        latestVersion: '1.0.0',
        updateUrl: 'https://apps.apple.com/app/id6443586861',
        forceUpdate: false
      }
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/admin/settings'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Convert ipWhitelist array to string for display
          const settingsData = { ...data.data };
          if (settingsData.security && Array.isArray(settingsData.security.ipWhitelist)) {
            settingsData.security.ipWhitelist = settingsData.security.ipWhitelist.join(', ');
          }
          setSettings(settingsData);
        }
      } else {
        console.error('Failed to fetch settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingChange = (category, setting, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [setting]: value
      }
    }));
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setSaveStatus('');

    try {
      const token = localStorage.getItem('token');

      // Prepare settings data for API
      const settingsData = { ...settings };

      // Convert ipWhitelist string back to array for backend
      if (settingsData.security.ipWhitelist && typeof settingsData.security.ipWhitelist === 'string') {
        settingsData.security.ipWhitelist = settingsData.security.ipWhitelist
          .split(',')
          .map(ip => ip.trim())
          .filter(ip => ip.length > 0);
      }

      const response = await fetch(buildApiUrl('/api/admin/settings'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          category: activeTab,
          settings: settingsData[activeTab]
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setSaveStatus('success');
          // Refresh settings to get updated data
          await fetchSettings();
        } else {
          setSaveStatus('error');
        }
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleResetSettings = (category) => {
    // Reset to default values
    const defaultSettings = {
      general: {
        siteName: 'MarryCenter',
        siteDescription: 'Find Your Perfect Match',
        maintenanceMode: false,
        allowNewRegistrations: true,
        requireEmailVerification: true,
        maxUsersPerDay: 1000,
        defaultMembershipType: 'free'
      },
      security: {
        passwordMinLength: 8,
        sessionTimeout: 24,
        maxLoginAttempts: 5,
        lockoutDuration: 30,
        twoFactorAuth: false,
        ipWhitelist: '',
        allowedFileTypes: 'jpg,jpeg,png,pdf',
        maxFileSize: 5
      },
      notifications: {
        emailNotifications: true,
        pushNotifications: true,
        smsNotifications: false,
        welcomeEmail: true,
        matchNotifications: true,
        messageNotifications: true,
        profileViewNotifications: false,
        systemAlerts: true
      },
      messaging: {
        maxMessageLength: 1000,
        messageRetentionDays: 365,
        allowImageMessages: true,
        autoDeleteOldMessages: false,
        messageFiltering: true,
        blockInappropriateContent: true,
        rateLimitPerHour: 50
      },
      backup: {
        autoBackup: true,
        backupFrequency: 'daily',
        backupRetention: 30,
        backupLocation: 'cloud',
        emailBackupReports: true,
        includeUserMedia: true
      },
      mobileApp: {
        android: {
          minVersionCode: 1,
          latestVersion: '1.0.0',
          updateUrl: 'https://play.google.com/store/apps/details?id=com.marrycenter.app',
          forceUpdate: false
        },
        ios: {
          minVersionCode: 1,
          latestVersion: '1.0.0',
          updateUrl: 'https://apps.apple.com/app/id6443586861',
          forceUpdate: false
        }
      }
    };

    if (category && defaultSettings[category]) {
      setSettings(prev => ({
        ...prev,
        [category]: defaultSettings[category]
      }));
    }
  };

  const handleBackupNow = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/admin/settings/backup'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `system_backup_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setSaveStatus('backup-completed');
      } else {
        console.error('Backup failed');
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      setSaveStatus('error');
    } finally {
      setIsLoading(false);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const tabs = [
    { id: 'general', label: 'General', icon: FiSettings },
    { id: 'security', label: 'Security', icon: FiShield },
    { id: 'notifications', label: 'Notifications', icon: FiBell },
    { id: 'messaging', label: 'Messaging', icon: FiMail },
    { id: 'mobileApp', label: 'Mobile App', icon: FiSmartphone },
    { id: 'backup', label: 'Backup', icon: FiDatabase }
  ];

  const ToggleSwitch = ({ checked, onChange, label }) => (
    <div className="flex items-center">
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-red-600' : 'bg-gray-200'
          }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'
            }`}
        />
      </button>
      <span className="ml-3 text-sm text-gray-700">{label}</span>
    </div>
  );

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">System Settings</h1>
          <p className="text-gray-600">Configure and manage platform settings and controls</p>
        </div>

        {/* Save Status */}
        {saveStatus && (
          <div className={`mb-6 p-4 rounded-lg border ${saveStatus === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : saveStatus === 'backup-completed'
              ? 'bg-blue-50 border-blue-200 text-blue-800'
              : 'bg-red-50 border-red-200 text-red-800'
            }`}>
            <div className="flex items-center">
              {saveStatus === 'success' ? (
                <FiSave className="w-5 h-5 mr-2" />
              ) : saveStatus === 'backup-completed' ? (
                <FiDatabase className="w-5 h-5 mr-2" />
              ) : (
                <FiAlertCircle className="w-5 h-5 mr-2" />
              )}
              {saveStatus === 'success'
                ? 'Settings saved successfully!'
                : saveStatus === 'backup-completed'
                  ? 'Backup completed successfully!'
                  : 'Error saving settings'
              }
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <div className="flex items-center">
                    <tab.icon className="w-4 h-4 mr-2" />
                    {tab.label}
                  </div>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* General Settings */}
            {activeTab === 'general' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">General Configuration</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Site Name
                      </label>
                      <input
                        type="text"
                        value={settings.general.siteName}
                        onChange={(e) => handleSettingChange('general', 'siteName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Site Description
                      </label>
                      <input
                        type="text"
                        value={settings.general.siteDescription}
                        onChange={(e) => handleSettingChange('general', 'siteDescription', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Users Per Day
                      </label>
                      <input
                        type="number"
                        value={settings.general.maxUsersPerDay}
                        onChange={(e) => handleSettingChange('general', 'maxUsersPerDay', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Membership Type
                      </label>
                      <select
                        value={settings.general.defaultMembershipType}
                        onChange={(e) => handleSettingChange('general', 'defaultMembershipType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value="free">Free</option>
                        <option value="premium">Premium</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-4 mt-6">
                    <ToggleSwitch
                      checked={settings.general.maintenanceMode}
                      onChange={(value) => handleSettingChange('general', 'maintenanceMode', value)}
                      label="Maintenance Mode"
                    />

                    <ToggleSwitch
                      checked={settings.general.allowNewRegistrations}
                      onChange={(value) => handleSettingChange('general', 'allowNewRegistrations', value)}
                      label="Allow New Registrations"
                    />

                    <ToggleSwitch
                      checked={settings.general.requireEmailVerification}
                      onChange={(value) => handleSettingChange('general', 'requireEmailVerification', value)}
                      label="Require Email Verification"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Security Configuration</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password Min Length
                      </label>
                      <input
                        type="number"
                        value={settings.security.passwordMinLength}
                        onChange={(e) => handleSettingChange('security', 'passwordMinLength', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Session Timeout (hours)
                      </label>
                      <input
                        type="number"
                        value={settings.security.sessionTimeout}
                        onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Login Attempts
                      </label>
                      <input
                        type="number"
                        value={settings.security.maxLoginAttempts}
                        onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Lockout Duration (minutes)
                      </label>
                      <input
                        type="number"
                        value={settings.security.lockoutDuration}
                        onChange={(e) => handleSettingChange('security', 'lockoutDuration', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max File Size (MB)
                      </label>
                      <input
                        type="number"
                        value={settings.security.maxFileSize}
                        onChange={(e) => handleSettingChange('security', 'maxFileSize', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Allowed File Types
                      </label>
                      <input
                        type="text"
                        value={settings.security.allowedFileTypes}
                        onChange={(e) => handleSettingChange('security', 'allowedFileTypes', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                        placeholder="jpg,jpeg,png,pdf"
                      />
                    </div>
                  </div>

                  <div className="space-y-4 mt-6">
                    <ToggleSwitch
                      checked={settings.security.twoFactorAuth}
                      onChange={(value) => handleSettingChange('security', 'twoFactorAuth', value)}
                      label="Two-Factor Authentication"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Notification Preferences</h3>

                  <div className="space-y-4">
                    <ToggleSwitch
                      checked={settings.notifications.emailNotifications}
                      onChange={(value) => handleSettingChange('notifications', 'emailNotifications', value)}
                      label="Email Notifications"
                    />

                    <ToggleSwitch
                      checked={settings.notifications.pushNotifications}
                      onChange={(value) => handleSettingChange('notifications', 'pushNotifications', value)}
                      label="Push Notifications"
                    />

                    <ToggleSwitch
                      checked={settings.notifications.smsNotifications}
                      onChange={(value) => handleSettingChange('notifications', 'smsNotifications', value)}
                      label="SMS Notifications"
                    />

                    <ToggleSwitch
                      checked={settings.notifications.welcomeEmail}
                      onChange={(value) => handleSettingChange('notifications', 'welcomeEmail', value)}
                      label="Welcome Email"
                    />

                    <ToggleSwitch
                      checked={settings.notifications.matchNotifications}
                      onChange={(value) => handleSettingChange('notifications', 'matchNotifications', value)}
                      label="Match Notifications"
                    />

                    <ToggleSwitch
                      checked={settings.notifications.messageNotifications}
                      onChange={(value) => handleSettingChange('notifications', 'messageNotifications', value)}
                      label="Message Notifications"
                    />

                    <ToggleSwitch
                      checked={settings.notifications.profileViewNotifications}
                      onChange={(value) => handleSettingChange('notifications', 'profileViewNotifications', value)}
                      label="Profile View Notifications"
                    />

                    <ToggleSwitch
                      checked={settings.notifications.systemAlerts}
                      onChange={(value) => handleSettingChange('notifications', 'systemAlerts', value)}
                      label="System Alerts"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Messaging Settings */}
            {activeTab === 'messaging' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Messaging Configuration</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Max Message Length
                      </label>
                      <input
                        type="number"
                        value={settings.messaging.maxMessageLength}
                        onChange={(e) => handleSettingChange('messaging', 'maxMessageLength', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message Retention Days
                      </label>
                      <input
                        type="number"
                        value={settings.messaging.messageRetentionDays}
                        onChange={(e) => handleSettingChange('messaging', 'messageRetentionDays', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rate Limit Per Hour
                      </label>
                      <input
                        type="number"
                        value={settings.messaging.rateLimitPerHour}
                        onChange={(e) => handleSettingChange('messaging', 'rateLimitPerHour', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="space-y-4 mt-6">
                    <ToggleSwitch
                      checked={settings.messaging.allowImageMessages}
                      onChange={(value) => handleSettingChange('messaging', 'allowImageMessages', value)}
                      label="Allow Image Messages"
                    />

                    <ToggleSwitch
                      checked={settings.messaging.autoDeleteOldMessages}
                      onChange={(value) => handleSettingChange('messaging', 'autoDeleteOldMessages', value)}
                      label="Auto Delete Old Messages"
                    />

                    <ToggleSwitch
                      checked={settings.messaging.messageFiltering}
                      onChange={(value) => handleSettingChange('messaging', 'messageFiltering', value)}
                      label="Message Filtering"
                    />

                    <ToggleSwitch
                      checked={settings.messaging.blockInappropriateContent}
                      onChange={(value) => handleSettingChange('messaging', 'blockInappropriateContent', value)}
                      label="Block Inappropriate Content"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Mobile App Settings */}
            {activeTab === 'mobileApp' && (
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Android Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Version Code</label>
                      <input
                        type="number"
                        value={settings.mobileApp.android.minVersionCode}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setSettings(prev => ({
                            ...prev,
                            mobileApp: {
                              ...prev.mobileApp,
                              android: { ...prev.mobileApp.android, minVersionCode: val }
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <p className="mt-1 text-xs text-gray-500">Must match versionCode in app.json</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Latest Version Name</label>
                      <input
                        type="text"
                        value={settings.mobileApp.android.latestVersion}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSettings(prev => ({
                            ...prev,
                            mobileApp: {
                              ...prev.mobileApp,
                              android: { ...prev.mobileApp.android, latestVersion: val }
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="e.g. 1.0.17"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Play Store URL</label>
                      <input
                        type="text"
                        value={settings.mobileApp.android.updateUrl}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSettings(prev => ({
                            ...prev,
                            mobileApp: {
                              ...prev.mobileApp,
                              android: { ...prev.mobileApp.android, updateUrl: val }
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="pt-2">
                      <ToggleSwitch
                        checked={settings.mobileApp.android.forceUpdate}
                        onChange={(value) => {
                          setSettings(prev => ({
                            ...prev,
                            mobileApp: {
                              ...prev.mobileApp,
                              android: { ...prev.mobileApp.android, forceUpdate: value }
                            }
                          }));
                        }}
                        label="Force Update (Requires Update to login)"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">iOS Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Version Code</label>
                      <input
                        type="number"
                        value={settings.mobileApp.ios.minVersionCode}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          setSettings(prev => ({
                            ...prev,
                            mobileApp: {
                              ...prev.mobileApp,
                              ios: { ...prev.mobileApp.ios, minVersionCode: val }
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Latest Version Name</label>
                      <input
                        type="text"
                        value={settings.mobileApp.ios.latestVersion}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSettings(prev => ({
                            ...prev,
                            mobileApp: {
                              ...prev.mobileApp,
                              ios: { ...prev.mobileApp.ios, latestVersion: val }
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">App Store URL</label>
                      <input
                        type="text"
                        value={settings.mobileApp.ios.updateUrl}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSettings(prev => ({
                            ...prev,
                            mobileApp: {
                              ...prev.mobileApp,
                              ios: { ...prev.mobileApp.ios, updateUrl: val }
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                    <div className="pt-2">
                      <ToggleSwitch
                        checked={settings.mobileApp.ios.forceUpdate}
                        onChange={(value) => {
                          setSettings(prev => ({
                            ...prev,
                            mobileApp: {
                              ...prev.mobileApp,
                              ios: { ...prev.mobileApp.ios, forceUpdate: value }
                            }
                          }));
                        }}
                        label="Force Update (Requires Update to login)"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Backup Settings */}
            {activeTab === 'backup' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Backup Configuration</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Backup Frequency
                      </label>
                      <select
                        value={settings.backup.backupFrequency}
                        onChange={(e) => handleSettingChange('backup', 'backupFrequency', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value="hourly">Hourly</option>
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Backup Retention (days)
                      </label>
                      <input
                        type="number"
                        value={settings.backup.backupRetention}
                        onChange={(e) => handleSettingChange('backup', 'backupRetention', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Backup Location
                      </label>
                      <select
                        value={settings.backup.backupLocation}
                        onChange={(e) => handleSettingChange('backup', 'backupLocation', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      >
                        <option value="local">Local Storage</option>
                        <option value="cloud">Cloud Storage</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-4 mt-6">
                    <ToggleSwitch
                      checked={settings.backup.autoBackup}
                      onChange={(value) => handleSettingChange('backup', 'autoBackup', value)}
                      label="Automatic Backup"
                    />

                    <ToggleSwitch
                      checked={settings.backup.emailBackupReports}
                      onChange={(value) => handleSettingChange('backup', 'emailBackupReports', value)}
                      label="Email Backup Reports"
                    />

                    <ToggleSwitch
                      checked={settings.backup.includeUserMedia}
                      onChange={(value) => handleSettingChange('backup', 'includeUserMedia', value)}
                      label="Include User Media"
                    />
                  </div>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <button
                      onClick={handleBackupNow}
                      disabled={isLoading}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center"
                    >
                      <FiRefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                      {isLoading ? 'Creating Backup...' : 'Backup Now'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <button
            onClick={() => handleResetSettings(activeTab)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Reset to Defaults
          </button>

          <div className="flex gap-3">
            <button
              onClick={handleSaveSettings}
              disabled={isLoading || isSaving}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
            >
              <FiSave className={`w-4 h-4 mr-2 ${isSaving ? 'animate-pulse' : ''}`} />
              {isSaving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default SystemSettings;
